import React, { useMemo, useImperativeHandle } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n/useTranslation';
import { SwipeActionProvider } from './SwipeAction.provider';
import { SwipeActionContent } from './components/SwipeActionContent';
import { SwipeActionAction } from './components/SwipeActionAction';
import { useSwipeAction } from './useSwipeAction';
import type { SwipeActionProps, SwipeActionRef } from './SwipeAction.types';

const SwipeActionInner: React.ForwardRefRenderFunction<SwipeActionRef, SwipeActionProps> = (
  { threshold = 50, duration = 200, stayOpen = true, disabled = false, onOpen, onClose, borderRadius, children },
  ref,
) => {
  const { tq } = useTranslation('sharedComponents');
  const { handlers, contentRef, actionsRef, isOpen, open, close } = useSwipeAction({
    threshold,
    duration,
    disabled,
    borderRadius,
    onOpen,
    onClose,
  });

  useImperativeHandle(ref, () => ({ close, isOpen }), [close, isOpen]);

  const contextValue = useMemo(
    () => ({ isOpen, disabled, contentRef, actionsRef, open, close, stayOpen, borderRadius }),
    [isOpen, disabled, contentRef, actionsRef, open, close, stayOpen, borderRadius],
  );

  // Separate action children from content children
  const contentChildren: React.ReactNode[] = [];
  const actionChildren: React.ReactNode[] = [];

  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && child.type === SwipeActionAction) {
      actionChildren.push(child);
    } else {
      contentChildren.push(child);
    }
  });

  return (
    <SwipeActionProvider value={contextValue}>
      <Box
        position={disabled ? undefined : 'relative'}
        touchAction={disabled ? undefined : 'pan-y'}
        {...(disabled ? {} : handlers)}
      >
        {contentChildren}
        {!disabled && actionChildren.length > 0 && (
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            alignItems="stretch"
            justifyContent="flex-end"
            visibility="hidden"
            role="group"
            aria-label={tq`Actions`}
          >
            <Box
              ref={actionsRef}
              display="grid"
              gridAutoColumns="1fr"
              gridAutoFlow="column"
              height="100%"
              borderTopRightRadius={borderRadius}
              borderBottomRightRadius={borderRadius}
              overflow="hidden"
            >
              {actionChildren}
            </Box>
          </Flex>
        )}
      </Box>
    </SwipeActionProvider>
  );
};

const SwipeActionWithRef = React.forwardRef(SwipeActionInner);

type SwipeActionComponent = typeof SwipeActionWithRef & {
  Content: typeof SwipeActionContent;
  Action: typeof SwipeActionAction;
};

const SwipeAction = SwipeActionWithRef as SwipeActionComponent;

SwipeAction.Content = SwipeActionContent;
SwipeAction.Action = SwipeActionAction;

export { SwipeAction };
